'use client';

import { logger } from '@/lib/utils/logger';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// API base URL - uses proxy in development
const API_BASE = '/api/auth';

// Legacy localStorage key to clean up
const LEGACY_AUTH_TOKEN_KEY = 'isLoggedIn';

interface User {
  id: string;
  email: string;
  name?: string | null;
  roles: string[];
  walletAddress?: string;
  walletChain?: 'ethereum' | 'bitcoin';
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  isHydrated: boolean;
  isLoading: boolean;
  // Magic Link methods
  sendMagicLink: (email: string) => Promise<{ success: boolean; error?: string }>;
  verifyMagicLink: (token: string) => Promise<{ success: boolean; user?: User; error?: string }>;
  // Session methods
  checkSession: () => Promise<void>;
  logout: () => Promise<void>;
  // Legacy methods (for compatibility)
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  // Direct user setter (for verify flow)
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to clean up legacy localStorage tokens (XSS vulnerability fix)
function cleanupLegacyTokens(): void {
  if (typeof window === 'undefined') return;
  try {
    // Clean up legacy localStorage tokens
    localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
    localStorage.removeItem('payload-token');
    localStorage.removeItem('userData');
  } catch (error) {
    logger.error('Failed to clean up legacy tokens:', error);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Check current session with backend using httpOnly cookie
   */
  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Uses httpOnly cookie for authentication
      });

      // Handle non-OK responses before parsing JSON
      if (!response.ok) {
        logger.warn('Session check failed:', { status: response.status, statusText: response.statusText });
        setUser(null);
        return;
      }

      // Safely parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        logger.error('Failed to parse session response:', parseError);
        setUser(null);
        return;
      }

      if (data.success && data.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      logger.error('Failed to check session:', error);
      setUser(null);
    }
  }, []);

  // Check session on mount and clean up legacy localStorage tokens
  useEffect(() => {
    const initAuth = async () => {
      // Clean up legacy localStorage tokens (XSS vulnerability fix)
      cleanupLegacyTokens();

      // Always check session via httpOnly cookie
      await checkSession();
      setIsHydrated(true);
    };

    initAuth();
  }, [checkSession]);

  /**
   * Send magic link to email
   */
  const sendMagicLink = async (email: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/magic-link/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to send magic link' };
      }

      return { success: true };
    } catch (error) {
      logger.error('Failed to send magic link:', error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verify magic link token
   */
  const verifyMagicLink = async (token: string): Promise<{ success: boolean; user?: User; error?: string }> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/magic-link/verify?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // httpOnly cookie set by backend
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to verify magic link' };
      }

      // Set user (httpOnly cookie already set by backend)
      if (data.user) {
        setUser(data.user);
        return { success: true, user: data.user };
      }

      return { success: false, error: 'No user data received' };
    } catch (error) {
      logger.error('Failed to verify magic link:', error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout - clear session (backend clears httpOnly cookie)
   */
  const logout = async () => {
    setIsLoading(true);
    try {
      await fetch(`${API_BASE}/me`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'logout' }),
        credentials: 'include',
      });
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      setUser(null);
      // Clear all cached queries so next user sees fresh data
      queryClient.clear();
      // Clean up any legacy localStorage tokens
      cleanupLegacyTokens();
      setIsLoading(false);
    }
  };

  /**
   * Legacy login method (for backward compatibility)
   * Now just sends magic link
   */
  const login = async (email: string, _password: string) => {
    const result = await sendMagicLink(email);
    if (!result.success) {
      throw new Error(result.error || 'Failed to send magic link');
    }
  };

  /**
   * Legacy signup method (for backward compatibility)
   * Magic link handles both login and signup
   */
  const signup = async (email: string, _password: string, _firstName: string, _lastName: string) => {
    const result = await sendMagicLink(email);
    if (!result.success) {
      throw new Error(result.error || 'Failed to send magic link');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn: !!user,
        isHydrated,
        isLoading,
        sendMagicLink,
        verifyMagicLink,
        checkSession,
        logout,
        login,
        signup,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export type { User };
